import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { GraduationCap, UserRound, BookOpen, MessageCircle } from 'lucide-react';
import { buildWhatsAppUrl, trackWhatsAppClick } from '@/lib/whatsapp';

const CHOICES = [
  {
    key: 'find_tutor',
    title: 'Find a Tutor',
    description: 'I need help with a subject',
    icon: GraduationCap,
  },
  {
    key: 'become_tutor',
    title: 'Become a Tutor',
    description: 'I want to join the network',
    icon: UserRound,
  },
  {
    key: 'resources',
    title: 'Study Resources',
    description: 'I need materials & guidance',
    icon: BookOpen,
  },
];

/**
 * Lightweight "How can we help?" picker.
 *
 * Wraps any trigger element and, on click, opens a small dialog with three
 * intent buttons. Each button opens wa.me with a context-tailored message
 * (via the shared helper in lib/whatsapp.js) and logs a click event.
 *
 * Usage:
 *   <WhatsAppIntentDialog source="floating-button">
 *     <a href="#">My trigger</a>
 *   </WhatsAppIntentDialog>
 */
const WhatsAppIntentDialog = ({ children, source = 'whatsapp-generic' }) => {
  const [open, setOpen] = useState(false);

  const handleChoose = (variant) => {
    trackWhatsAppClick(source, variant);
    // Open in new tab on desktop; mobile will deep-link into the app.
    window.open(buildWhatsAppUrl({ variant }), '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="sm:max-w-md bg-white"
        data-testid="whatsapp-intent-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <MessageCircle className="h-5 w-5 text-[#25D366]" />
            How can we help you?
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-1">
            Pick what suits you — we'll open WhatsApp with a pre-filled message
            so our team can help you faster.
          </p>
        </DialogHeader>
        <div className="mt-4 space-y-2">
          {CHOICES.map(({ key, title, description, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => handleChoose(key)}
              className="w-full flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-[#25D366] hover:bg-green-50/60 transition-colors text-left"
              data-testid={`whatsapp-intent-${key}`}
            >
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-green-50 text-[#25D366] shrink-0">
                <Icon className="h-5 w-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-gray-900">
                  {title}
                </span>
                <span className="block text-sm text-gray-500">
                  {description}
                </span>
              </span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsAppIntentDialog;
